import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { qk } from '@/lib/query';
import { ApiError } from '@/lib/api';
import * as importsApi from '@/api/imports';
import type { ImportBatchDetail, ImportMatch } from '@/types';

export function useImports() {
  return useQuery({
    queryKey: qk.imports,
    queryFn: ({ signal }) => importsApi.listImports(signal)
  });
}

export function usePendingImport() {
  return useQuery({
    queryKey: qk.importsPending,
    queryFn: ({ signal }) => importsApi.pendingImport(signal),
    staleTime: 30_000
  });
}

export function useImport(id: string | undefined) {
  return useQuery({
    queryKey: qk.import(id ?? ''),
    queryFn: ({ signal }) => importsApi.getImport(id as string, signal),
    enabled: !!id
  });
}

/**
 * Accepting or undoing a batch creates or deletes rows in every table the
 * app reads, so the whole working set is refetched, the setup rail included.
 */
function invalidateAfterImport(qc: QueryClient, id?: string) {
  void qc.invalidateQueries({ queryKey: qk.imports });
  void qc.invalidateQueries({ queryKey: qk.importsPending });
  if (id) void qc.invalidateQueries({ queryKey: qk.import(id) });
  void qc.invalidateQueries({ queryKey: qk.clients });
  void qc.invalidateQueries({ queryKey: qk.billables });
  void qc.invalidateQueries({ queryKey: qk.activities });
  void qc.invalidateQueries({ queryKey: qk.daycardsRoot });
  void qc.invalidateQueries({ queryKey: qk.invoicesRoot });
  void qc.invalidateQueries({ queryKey: qk.setup });
}

const REFUSALS: Record<string, string> = {
  unmatched: 'Some clients are not matched yet. Match or skip them first.',
  not_pending: 'This import is no longer pending.',
  not_accepted: 'This import was never accepted.',
  invoiced: 'Some imported entries have been invoiced since.',
  in_use: 'Some imported records are in use elsewhere.',
  expired: 'The undo window for this import has closed.'
};

/**
 * A 409 carries `{ refused }` in its body: the reason the server would not
 * accept or undo. Everything else falls back to the error's message.
 */
/** Accept refused because some clients still match nothing. */
export function isUnmatchedRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    (error.body as { refused?: string } | undefined)?.refused === 'unmatched'
  );
}

function refusalText(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    const body = error.body as { refused?: string; error?: unknown } | undefined;
    const refused = body?.refused;
    if (error.status === 409 && refused) return REFUSALS[refused] ?? refused;
    return error.message;
  }
  return error instanceof Error ? error.message : undefined;
}

function acceptedEntries(result: { counts?: Record<string, number> } | undefined, fallback?: number) {
  const n = result?.counts?.entries ?? fallback ?? 0;
  return `${n} ${n === 1 ? 'entry' : 'entries'} accepted. Undo for 7 days`;
}

export function useStageImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: importsApi.StageImportInput) => importsApi.stageImport(input),
    onSuccess() {
      void qc.invalidateQueries({ queryKey: qk.imports });
      void qc.invalidateQueries({ queryKey: qk.importsPending });
      void qc.invalidateQueries({ queryKey: qk.setup });
    },
    onError(error: unknown) {
      toast.error('Could not stage that import.', { description: refusalText(error) });
    }
  });
}

export function useSetMatch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ target, match }: { target: importsApi.MatchTarget; match: ImportMatch }) =>
      importsApi.setMatch(id, target, match),
    onSuccess() {
      void qc.invalidateQueries({ queryKey: qk.import(id) });
      void qc.invalidateQueries({ queryKey: qk.importsPending });
    },
    onError(error: unknown) {
      toast.error('Could not update that match.', { description: refusalText(error) });
    }
  });
}

export function useUndoImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => importsApi.undoImport(id),
    onSuccess(_result, id) {
      invalidateAfterImport(qc, id);
      toast.success('Import undone');
    },
    onError(error: unknown) {
      toast.error('Could not undo that import.', { description: refusalText(error) });
    }
  });
}

export function useAcceptImport() {
  const qc = useQueryClient();
  const undo = useUndoImport();
  return useMutation({
    mutationFn: ({ id, mode = 'all' }: { id: string; mode?: importsApi.AcceptMode }) =>
      importsApi.acceptImport(id, mode),
    onSuccess(result, { id }) {
      invalidateAfterImport(qc, id);
      const pending = qc.getQueryData<ImportBatchDetail>(qk.import(id));
      toast.success(acceptedEntries(result, pending?.summary?.counts?.entry), {
        action: { label: 'Undo', onClick: () => undo.mutate(id) }
      });
    },
    onError(error: unknown) {
      toast.error('Could not accept that import.', { description: refusalText(error) });
    }
  });
}

export function useDiscardImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => importsApi.discardImport(id),
    onSuccess(_result, id) {
      invalidateAfterImport(qc, id);
      toast.success('Import discarded');
    },
    onError(error: unknown) {
      toast.error('Could not discard that import.', { description: refusalText(error) });
    }
  });
}

export function useAiMapColumns() {
  return useMutation({
    mutationFn: (input: { headers: string[]; sample: string[][] }) => importsApi.aiMapColumns(input)
  });
}

export function useAiScan() {
  return useMutation({
    mutationFn: (input: { image: string; mime: string; kind: 'timesheet' | 'invoice' }) =>
      importsApi.aiScan(input)
  });
}
